import { useMemo, useState } from "react";
import { useI18n } from "../../i18n";
import { createId } from "../../lib/id";
import { removeTodo, saveTodo } from "../../lib/storage";
import type { TodoItem } from "../../types";

function dateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function monthDays(cursor: Date): Date[] {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
}

export function Calendar({
  todos,
  onRefresh,
}: {
  todos: TodoItem[];
  onRefresh: () => Promise<void>;
}): React.JSX.Element {
  const { language, t } = useI18n();
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [selectedDate, setSelectedDate] = useState(dateKey(today));
  const [zoom, setZoom] = useState(1);
  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState("");
  const [time, setTime] = useState("09:00");
  const [kind, setKind] = useState<TodoItem["kind"]>("todo");
  const [notice, setNotice] = useState("");
  const days = monthDays(cursor);
  const selectedItems = todos.filter((todo) => todo.dueDate === selectedDate);
  const byDate = useMemo(() => {
    const grouped = new Map<string, TodoItem[]>();
    todos.forEach((todo) => {
      grouped.set(todo.dueDate, [...(grouped.get(todo.dueDate) ?? []), todo]);
    });
    return grouped;
  }, [todos]);

  const moveMonth = (offset: number): void => {
    setCursor(
      (current) =>
        new Date(current.getFullYear(), current.getMonth() + offset, 1),
    );
  };
  const goToday = (): void => {
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(dateKey(today));
  };
  const add = async (): Promise<void> => {
    if (!title.trim()) return;
    await saveTodo({
      id: createId(),
      title: title.trim(),
      owner: owner.trim(),
      dueDate: selectedDate,
      time,
      kind,
      completed: false,
      notes: "",
      source: "manual",
      sourceMeetingId: null,
      createdAt: new Date().toISOString(),
    });
    setTitle("");
    setOwner("");
    setNotice(t("calendar.saved"));
    await onRefresh();
  };
  const toggle = async (todo: TodoItem): Promise<void> => {
    await saveTodo({ ...todo, completed: !todo.completed });
    await onRefresh();
  };

  return (
    <div className="calendar-layout">
      <section className="panel calendar-panel">
        <div className="calendar-toolbar">
          <div className="calendar-nav">
            <button
              className="secondary icon-button"
              onClick={() => moveMonth(-1)}
            >
              ←
            </button>
            <button className="secondary" onClick={goToday}>
              {t("calendar.today")}
            </button>
            <button
              className="secondary icon-button"
              onClick={() => moveMonth(1)}
            >
              →
            </button>
          </div>
          <h2>
            {new Intl.DateTimeFormat(language, {
              month: "long",
              year: "numeric",
            }).format(cursor)}
          </h2>
          <div className="zoom-control" aria-label={t("calendar.zoom")}>
            <button
              type="button"
              onClick={() => setZoom((value) => Math.max(0.8, value - 0.1))}
              aria-label={t("calendar.zoomOut")}
            >
              −
            </button>
            <span>{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              onClick={() => setZoom((value) => Math.min(1.2, value + 0.1))}
              aria-label={t("calendar.zoomIn")}
            >
              +
            </button>
          </div>
        </div>
        <div className="calendar-viewport">
          <div
            className="calendar-surface"
            style={{
              transform: `scale(${zoom})`,
              width: `${100 / zoom}%`,
            }}
          >
            <div className="weekday-row">
              {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                <span key={day}>
                  {new Intl.DateTimeFormat(language, {
                    weekday: "short",
                  }).format(new Date(2026, 0, 4 + day))}
                </span>
              ))}
            </div>
            <div className="month-grid">
              {days.map((date) => {
                const key = dateKey(date);
                const items = byDate.get(key) ?? [];
                const outside = date.getMonth() !== cursor.getMonth();
                return (
                  <button
                    type="button"
                    className={`calendar-day${outside ? " outside" : ""}${key === selectedDate ? " selected" : ""}${key === dateKey(today) ? " today" : ""}`}
                    key={key}
                    onClick={() => setSelectedDate(key)}
                  >
                    <span className="day-number">{date.getDate()}</span>
                    <span className="day-events">
                      {items.slice(0, 2).map((item) => (
                        <span
                          className={`${item.kind} ${item.completed ? "completed" : ""}`}
                          key={item.id}
                        >
                          {item.time && `${item.time} `}
                          {item.title}
                        </span>
                      ))}
                      {items.length > 2 && <small>+{items.length - 2}</small>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <aside className="panel day-panel">
        <small>{t("calendar.selectedDay")}</small>
        <h2>
          {new Intl.DateTimeFormat(language, { dateStyle: "full" }).format(
            new Date(`${selectedDate}T12:00:00`),
          )}
        </h2>
        <div className="day-agenda">
          {selectedItems.length === 0 ? (
            <p className="muted">{t("calendar.empty")}</p>
          ) : (
            selectedItems.map((todo) => (
              <div
                className={`agenda-item ${todo.completed ? "completed" : ""}`}
                key={todo.id}
              >
                <input
                  type="checkbox"
                  checked={todo.completed}
                  onChange={() => void toggle(todo)}
                  aria-label={t("calendar.complete")}
                />
                <div>
                  <strong>{todo.title}</strong>
                  <small>
                    {todo.time || t("calendar.allDay")} ·{" "}
                    {todo.owner || t("meeting.unassigned")}
                  </small>
                </div>
                <button
                  className="text-button danger-text"
                  type="button"
                  onClick={() =>
                    void removeTodo(todo.id).then(() => onRefresh())
                  }
                  aria-label={t("common.delete")}
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
        <div className="quick-add">
          <h3>{t("calendar.add")}</h3>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t("calendar.titlePlaceholder")}
          />
          <div className="quick-add-row">
            <input
              value={owner}
              onChange={(event) => setOwner(event.target.value)}
              placeholder={t("calendar.ownerPlaceholder")}
            />
            <input
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
            />
          </div>
          <div className="quick-add-row">
            <select
              value={kind}
              onChange={(event) =>
                setKind(event.target.value as TodoItem["kind"])
              }
            >
              <option value="todo">{t("calendar.todo")}</option>
              <option value="schedule">{t("calendar.schedule")}</option>
            </select>
            <button
              className="primary"
              type="button"
              disabled={!title.trim()}
              onClick={() => void add()}
            >
              {t("calendar.addButton")}
            </button>
          </div>
          {notice && <p className="notice success">{notice}</p>}
        </div>
      </aside>
    </div>
  );
}
